import { useLogger } from '../composables/useLogger'
import { NOINDEX_FOLLOW, NOINDEX_NOFOLLOW } from '../types/seo'
import type { SeoCanonical, SeoDraft, SeoImage, SeoRobots } from '../types/seo'

/**
 * SEO helpers shared by every app.
 *
 * Two halves that lean on each other:
 *
 * - **URL resolution** — everything a crawler reads (canonical, og:url, og:image, JSON-LD
 *   `url`/`image`) must be an absolute https URL. Authors write short relative paths, so these
 *   turn those into absolute URLs against the site profile `baseUrl`, and repair URLs that are
 *   already absolute.
 * - **Draft building** — the per-page half of `useSeo` (see `composables/seo/useSeo`): noindex
 *   shells, entity copy trimmed to a meta-safe length, and images dropped unless they are http(s).
 *
 * Pure functions only — no Vue, Pinia, or Nuxt context needed.
 */

// ===== URL resolution =====

/** Matches an absolute URL we can safely treat as already-resolved. */
const ABSOLUTE_HTTP_RE = /^https?:\/\//i
const ABSOLUTE_HTTPS_RE = /^https:\/\//i
const PROTOCOL_RELATIVE_RE = /^\/\//
const ANY_SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i

/** Canonicals identify a page, so a `#fragment` must never survive into one. */
function stripUrlFragment(url: string): string {
  const hashIndex = url.indexOf('#')
  return hashIndex === -1 ? url : url.slice(0, hashIndex)
}

/** Normalize a relative path: single leading slash, no query/hash, no trailing slash (except root). */
export function normalizeSeoPath(path: string): string {
  const clean = path.split(/[?#]/)[0]
  const withLead = `/${clean.replace(/^\/+/, '')}`
  return withLead.length > 1 ? withLead.replace(/\/+$/, '') : '/'
}

/**
 * Prefer https absolute URLs. Never throw — coerce http→https or use fallback.
 */
export function ensureAbsoluteHttps(url: string, fallbackUrl: string): string {
  const { logger } = useLogger()
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:') {
      return parsed.toString()
    }
    if (parsed.protocol === 'http:') {
      logger.error(`[seo] Absolute URL must be https (coercing http→https): ${url}`)
      parsed.protocol = 'https:'
      return parsed.toString()
    }
    logger.error(`[seo] Absolute URL must be https (using fallback): ${url}`)
    return fallbackUrl
  } catch {
    logger.error(`[seo] Invalid absolute URL (using fallback): ${url}`)
    return fallbackUrl
  }
}

/** Join `baseUrl` with a relative path. `?query` is kept, `#fragment` is dropped. */
function joinBaseAndPath(baseUrl: string, pathWithOptionalQuery: string): string {
  const [pathPart, queryPart] = stripUrlFragment(pathWithOptionalQuery).split('?')
  const normalized = normalizeSeoPath(pathPart ?? '/')
  return queryPart ? `${baseUrl}${normalized}?${queryPart}` : `${baseUrl}${normalized}`
}

/**
 * Resolve the canonical URL for a page.
 *
 * Accepted inputs:
 * - `'/pricing'` — relative path, joined to `baseUrl`
 * - `'https://example.com/pricing'` — absolute URL, kept as-is (http is coerced to https)
 * - `{ url, type: 'relative' | 'absolute' }` — same behaviour, stated explicitly
 *
 * A string that already looks absolute is treated as absolute regardless of a declared
 * `type: 'relative'`, because joining it to `baseUrl` would emit a broken canonical.
 */
export function resolveSeoCanonical(canonical: SeoCanonical | undefined, fallbackPath: string, baseUrl: string): string {
  const pathFallback = joinBaseAndPath(baseUrl, fallbackPath)
  if (!canonical) return pathFallback

  const url = (typeof canonical === 'string' ? canonical : canonical.url).trim()
  if (!url) return pathFallback

  const isAbsolute = ABSOLUTE_HTTP_RE.test(url) || (typeof canonical !== 'string' && canonical.type === 'absolute')
  if (isAbsolute) {
    return stripUrlFragment(ensureAbsoluteHttps(url, pathFallback))
  }

  return joinBaseAndPath(baseUrl, url)
}

export interface ResolveSeoAbsoluteUrlOptions {
  /**
   * When true, keep `?query` on relative paths (images / media assets).
   * Default false strips query/hash so entity/page URLs stay path-stable for crawl identity.
   */
  preserveQuery?: boolean
}

/** Resolve a relative path or protocol-relative URL against an https origin. Absolute https is returned as-is. */
export function resolveSeoAbsoluteUrl(url: string, baseUrl: string, options?: ResolveSeoAbsoluteUrlOptions): string {
  const trimmed = url.trim()
  if (!trimmed) return trimmed
  if (ABSOLUTE_HTTPS_RE.test(trimmed)) return ensureAbsoluteHttps(trimmed, baseUrl)
  if (PROTOCOL_RELATIVE_RE.test(trimmed)) return ensureAbsoluteHttps(`https:${trimmed}`, baseUrl)
  if (ANY_SCHEME_RE.test(trimmed)) {
    // Non-https schemes (mailto:, tel:, http:, data:) — leave untouched for schema flexibility
    return trimmed
  }
  if (options?.preserveQuery) {
    return joinBaseAndPath(baseUrl, trimmed)
  }
  return `${baseUrl}${normalizeSeoPath(trimmed)}`
}

/**
 * Resolve a head link href the same way as image/JSON-LD URLs:
 * absolute https and non-https schemes stay intact; relative paths join to `baseUrl`.
 * Query strings on relative paths are preserved via `resolveSeoCanonical`.
 */
export function resolveSeoHeadLinkHref(href: string, fallbackPath: string, baseUrl: string): string {
  const trimmed = href.trim()
  if (!trimmed) return resolveSeoCanonical(undefined, fallbackPath, baseUrl)
  if (ABSOLUTE_HTTPS_RE.test(trimmed) || PROTOCOL_RELATIVE_RE.test(trimmed) || ANY_SCHEME_RE.test(trimmed)) {
    return resolveSeoAbsoluteUrl(trimmed, baseUrl)
  }
  return resolveSeoCanonical(trimmed, fallbackPath, baseUrl)
}

/**
 * Entity / page URL keys. Includes BreadcrumbList `item` (a URL string in Google's
 * breadcrumb docs). ItemList `item` values that are objects are left alone — only strings
 * are absolutized below.
 */
const JSON_LD_ENTITY_URL_KEYS = new Set(['url', '@id', 'sameAs', 'item'])
/** Asset URL keys — query strings are load-bearing (CDN transforms / signed URLs). */
const JSON_LD_ASSET_URL_KEYS = new Set(['image', 'logo', 'contentUrl', 'thumbnailUrl'])

function absolutizeJsonLdValue(value: unknown, baseUrl: string, keyHint?: string): unknown {
  if (typeof value === 'string') {
    if (keyHint && JSON_LD_ASSET_URL_KEYS.has(keyHint)) {
      return resolveSeoAbsoluteUrl(value, baseUrl, { preserveQuery: true })
    }
    if (keyHint && JSON_LD_ENTITY_URL_KEYS.has(keyHint)) {
      // Strip query/hash — entity/page identifiers should stay path-stable for crawl signals.
      return resolveSeoAbsoluteUrl(value, baseUrl)
    }
    return value
  }
  if (Array.isArray(value)) {
    return value.map(item => absolutizeJsonLdValue(item, baseUrl, keyHint))
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      result[key] = absolutizeJsonLdValue(child, baseUrl, key)
    }
    return result
  }
  return value
}

/** Resolve relative `url` / `@id` / `image` / `sameAs` fields against `baseUrl` for Schema.org. */
export function absolutizeSeoJsonLd<T>(jsonLd: T, baseUrl: string): T {
  return absolutizeJsonLdValue(jsonLd, baseUrl) as T
}

/**
 * Allow only http(s) URLs for SEO media and `sameAs` social links.
 * Entity data reaches the head as-is, so anything that is not an http(s) URL is dropped.
 */
export function isSafeSeoHttpUrl(candidate: string | null | undefined): boolean {
  if (!candidate?.trim()) {
    return false
  }

  try {
    const parsed = new URL(candidate)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

// ===== Draft building =====

/** Google truncates around this width; keeping descriptions under it avoids ellipsized snippets. */
export const SEO_DESCRIPTION_MAX_LENGTH = 155

/** Collapse whitespace and trim to `max`, appending an ellipsis when the text was cut. */
export function truncateSeoDescription(text: string, max: number = SEO_DESCRIPTION_MAX_LENGTH): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trimEnd()}…`
}

/** Reduce stored rich text to a single line of plain text for meta descriptions. */
export function stripHtmlToSeoText(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Build an OG/Twitter image from entity data, dropping URLs a crawler must not be handed. */
export function buildSeoImage(url: string | null | undefined, alt: string): SeoImage | undefined {
  const trimmed = url?.trim()
  if (!trimmed || !isSafeSeoHttpUrl(trimmed)) return undefined
  return { url: trimmed, alt }
}

export interface NoindexSeoInput {
  title?: string
  canonical: SeoCanonical
  description?: string
  /** Social preview art. `noindex` does not stop link unfurls, so shells still want a real image. */
  image?: SeoImage
  /** Defaults to `true`; pass `false` for authenticated/app-only shells that must not leak link equity. */
  follow?: boolean
}

/**
 * SEO for an app surface that must never compete with public marketing pages in the index —
 * authenticated shells, builders, dashboards.
 *
 * Titles still matter: they show in the browser tab and in shared links. So does the image, since
 * `noindex` stops search indexing but not link unfurls.
 */
export function buildNoindexSeo(input: NoindexSeoInput): SeoDraft {
  const robots: SeoRobots = input.follow === false ? NOINDEX_NOFOLLOW : NOINDEX_FOLLOW
  return {
    title: input.title,
    description: input.description,
    image: input.image,
    canonical: input.canonical,
    robots,
  }
}
